import React, { useRef, useState } from 'react';
import Icon from '../shared/Icon';
import './FileUploadZone.css';

/**
 * Reusable drag-and-drop file upload zone.
 * Used by both the DataSourceWizard (Step2ConnectRawData) and EditDatasetModal.
 */
/**
 * PRD-1359 (feedback doc §Creating a New Dataset): "unsupported file format
 * upload attempts should be flagged as an error on step 1 not at the end of the
 * dataset creation flow".
 *
 * The `accept` attribute only filters the file PICKER — a drag-and-drop bypasses
 * it entirely, which is how an unsupported file reached the end of the wizard
 * before anything complained. Validating here catches both routes, and catches
 * them for the Edit drawer too, since it shares this component.
 *
 * Extension-based on purpose: the backend's supported-types config is a list of
 * extensions, and a dropped file's MIME type is unreliable (empty for many, and
 * wrong for CSV on several platforms).
 */
const acceptedExtensions = (supportedFileTypes) => {
  const raw = supportedFileTypes?.extensions?.length
    ? supportedFileTypes.extensions
    : (supportedFileTypes?.accept || '').split(',');
  return raw
    .map((e) => String(e).trim().toLowerCase())
    .filter(Boolean)
    .map((e) => (e.startsWith('.') ? e : `.${e}`));
};

const isSupported = (file, allowed) => {
  // No configured list (the endpoint failed and defaults were empty) — accept
  // rather than block every upload on a config problem.
  if (!allowed.length) return true;
  const name = (file.name || '').toLowerCase();
  return allowed.some((ext) => name.endsWith(ext));
};

const FileUploadZone = ({ selectedFiles = [], onFilesChanged, supportedFileTypes, compact = false }) => {
  const fileInputRef = useRef(null);
  // PRD-1359 (feedback doc §Creating a New Dataset): the zone had no drag-over
  // state, so a dragged file gave no sign it would be accepted. dragenter and
  // dragleave fire for every child element the pointer crosses, which makes a
  // boolean flicker — count the enters and leaves instead, and only clear when
  // the pointer has genuinely left the zone.
  const [dragDepth, setDragDepth] = useState(0);
  const isDragging = dragDepth > 0;
  // Names of files the user just tried to add that this dataset cannot take.
  const [rejected, setRejected] = useState([]);

  const allowed = acceptedExtensions(supportedFileTypes);

  // Keeps the supported files and names the rest, rather than dropping the whole
  // batch: a reviewer who drags a folder of twelve files and one .docx should
  // not lose the eleven good ones.
  const addFiles = (incoming) => {
    const ok = incoming.filter((f) => isSupported(f, allowed));
    const bad = incoming.filter((f) => !isSupported(f, allowed));
    setRejected(bad.map((f) => f.name));
    if (ok.length) onFilesChanged([...selectedFiles, ...ok]);
  };

  const handleFileSelect = (e) => {
    addFiles(Array.from(e.target.files));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragDepth(0);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    // Still required: without preventDefault the browser refuses the drop.
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragDepth((d) => d + 1);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  };

  const handleRemoveFile = (index) => {
    onFilesChanged(selectedFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="file-upload-zone-wrapper">
      <div
        className={`file-upload-zone-dropzone ${compact ? 'file-upload-zone-dropzone-compact' : ''}${isDragging ? ' file-upload-zone-dropzone-dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={supportedFileTypes?.accept || ''}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <Icon name="cloud_upload" className="file-upload-zone-icon" size={48} />
        <p className="file-upload-zone-text">
          Drag and drop files here, or click to browse
        </p>
        {supportedFileTypes?.labels && (
          <p className="file-upload-zone-hint">
            Supported formats: {supportedFileTypes.labels}
          </p>
        )}
      </div>

      {rejected.length > 0 && (
        <div className="resui-banner resui-banner--error file-upload-zone-rejected" role="alert">
          <Icon name="error_outline" />
          <span className="resui-banner-text">
            {rejected.length === 1
              ? `${rejected[0]} isn't a supported format`
              : `${rejected.length} files aren't a supported format: ${rejected.join(', ')}`}
            {supportedFileTypes?.labels ? `. Supported formats: ${supportedFileTypes.labels}.` : '.'}
          </span>
          <button
            type="button"
            className="resui-banner-close"
            aria-label="Dismiss"
            onClick={(e) => { e.stopPropagation(); setRejected([]); }}
          >
            <Icon name="close" />
          </button>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="file-upload-zone-file-list">
          <h4 className="file-upload-zone-file-list-title">Selected Files:</h4>
          {selectedFiles.map((file, index) => (
            <div key={`${file.name}-${file.size}-${file.lastModified}-${index}`} className="file-upload-zone-file-item">
              <Icon name="description" className="file-upload-zone-file-icon" size={20} />
              <span className="file-upload-zone-file-name">{file.name}</span>
              <span className="file-upload-zone-file-size">
                {(file.size / 1024).toFixed(2)} KB
              </span>
              <button
                className="file-upload-zone-file-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
              >
                <Icon name="close" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
